import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';

import Card from '@/components/Card';
import Page from '@/components/Page';
import Paginator from '@/components/Paginator/Paginator';
import Stack from '@/components/Stack';
import styled from '@/styles';
import {
  ActivationFunnelData,
  Pagination,
  Skill,
  TierBoundaryLite,
  WorkerListItem,
  WorkersListParams,
  WorkersV2API,
} from '@/services/workersV2API';

import WorkerCard from '../WorkersV2/components/WorkerCard';
import { normalizeWorkerSearch } from '../WorkersV2/helpers';
import FunnelSummary from './components/FunnelSummary';
import FilterRow, {
  EMPTY_FILTERS,
  GravyWorkersFilters,
} from './components/FilterRow';
import WorkersTable, { SortColumn, SortState } from './components/WorkersTable';
import { RADIUS, TAB_OPTIONS, TAB_STATE } from './mappings';

type Tab = (typeof TAB_OPTIONS)[number]['value'];

// The page column is `Stack vertical` (align-items: flex-start), so without an
// explicit width this row shrinks to its content and `space-between` spreads
// nothing (GRAVY-10815).
const Toolbar = styled('div', {
  display: 'flex',
  width: '100%',
  gap: '12px',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
});
const Search = styled('input', {
  height: '40px',
  width: '300px',
  maxWidth: '100%',
  padding: '0 12px 0 36px',
  border: '1px solid #DADADA',
  borderRadius: '8px',
  fontSize: '14px',
});
const Tabs = styled('div', {
  display: 'inline-flex',
  border: '1px solid #DADADA',
  borderRadius: '8px',
  padding: '4px',
  gap: '4px',
  '@media (max-width: 768px)': { flexWrap: 'wrap' },
});
// Right-aligned on the search row per the design, and still right-aligned when
// the row wraps at narrow desktop widths. Below 768px the group takes the full
// width and wraps, so the four tabs plus Show cannot push past the viewport.
const ToolbarActions = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  width: 'auto',
  marginLeft: 'auto',
  '@media (max-width: 768px)': {
    width: '100%',
    marginLeft: 0,
    flexWrap: 'wrap',
  },
});
const TabBtn = styled('button', {
  appearance: 'none',
  border: 'none',
  background: 'transparent',
  padding: '6px 12px',
  borderRadius: '6px',
  fontSize: '14px',
  cursor: 'pointer',
  variants: {
    selected: {
      true: { backgroundColor: '#1E8E3E', color: '#FFFFFF', fontWeight: 600 },
    },
  },
});
const ShowBtn = styled('button', {
  appearance: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  height: '40px',
  padding: '0 14px',
  border: '1px solid #DADADA',
  borderRadius: '8px',
  background: '#FFFFFF',
  cursor: 'pointer',
  fontSize: '14px',
  variants: { open: { true: { borderColor: '#1E8E3E', color: '#1E8E3E' } } },
});
const MobileList = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  padding: '12px',
});

// Quick tabs narrow to one pill state, so a tab and the pills it shows can
// never disagree (both come from Workers::CurrentStatus).
const tabParams = (tab: Tab): Partial<WorkersListParams> =>
  tab === 'all' ? {} : { current_status: TAB_STATE[tab] };

const GravyWorkers = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 768px)');

  const [funnel, setFunnel] = useState<ActivationFunnelData | null>(null);
  const [funnelLoading, setFunnelLoading] = useState(true);
  const [workers, setWorkers] = useState<WorkerListItem[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [boundaries, setBoundaries] = useState<TierBoundaryLite[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [skills, setSkills] = useState<Skill[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [tab, setTab] = useState<Tab>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<GravyWorkersFilters>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [sort, setSort] = useState<SortState>({
    by: 'created_at',
    direction: 'desc',
  });

  const requestSeq = useRef(0);

  useEffect(() => {
    WorkersV2API.getSkills()
      .then((r) => r.success && setSkills(r.data))
      .catch((err) => console.error('[GravyWorkers] Error:', err));
    WorkersV2API.getActivationFunnel()
      .then((r) => r.success && setFunnel(r.data))
      .catch((err) => console.error('[GravyWorkers] Error:', err))
      .finally(() => setFunnelLoading(false));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(1);
    }, 500);
    return () => clearTimeout(t);
  }, [searchInput]);

  const params = useMemo<WorkersListParams>(() => {
    const p: WorkersListParams = {
      page,
      per_page: perPage,
      sort_by: sort.by,
      sort_direction: sort.direction,
      ...tabParams(tab),
    };
    const search = normalizeWorkerSearch(debouncedSearch);
    if (search) p.search = search;
    if (filters.tax_type) p.tax_type = filters.tax_type;
    if (filters.skill_ids?.length) p.skill_ids = filters.skill_ids;
    if (filters.dependability_v2_tier)
      p.dependability_v2_tier = [filters.dependability_v2_tier];
    if (filters.last_active) p.last_active = filters.last_active;
    // PD-6: the design lists Disabled workers by default; the legacy API hides them.
    p.active = filters.active ? filters.active === 'true' : 'all';
    if (filters.accountability_status)
      p.accountability_status = [filters.accountability_status];
    if (filters.progress) p.progress = filters.progress;
    if (filters.zip && filters.zip.length === 5) {
      p.zip = filters.zip;
      p.radius = filters.radius || RADIUS.default;
    }
    return p;
  }, [page, perPage, sort, tab, debouncedSearch, filters]);

  const fetchWorkers = useCallback(async () => {
    const seq = ++requestSeq.current;
    setListLoading(true);
    try {
      setError(null);
      const res = await WorkersV2API.getWorkers(params);
      if (seq !== requestSeq.current) return;
      if (res.success) {
        setWorkers(res.data);
        setPagination(res.pagination);
        setBoundaries(res.meta?.tier_boundaries ?? []);
      } else {
        setError('Failed to fetch workers');
      }
    } catch (err) {
      if (seq !== requestSeq.current) return;
      console.error('[GravyWorkers] Error:', err);
      setError('Failed to fetch workers');
    } finally {
      if (seq === requestSeq.current) setListLoading(false);
    }
  }, [params]);

  useEffect(() => {
    fetchWorkers();
  }, [fetchWorkers]);

  const onSort = (column: SortColumn) => {
    setSort((s) => ({
      by: column,
      direction: s.by === column && s.direction === 'asc' ? 'desc' : 'asc',
    }));
    setPage(1);
  };

  const skillsForRow = useMemo(
    () => skills.map((s) => ({ id: s.id, name: s.display_name_en })),
    [skills],
  );

  return (
    <Page title="GravyWorkers">
      <Stack vertical gap={16}>
        <FunnelSummary data={funnel} loading={funnelLoading} />

        <Toolbar>
          <Search
            data-testid="searchInput"
            aria-label="Search gravyworker"
            placeholder="Search gravyworker"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <ToolbarActions>
            <Tabs role="tablist" aria-label="Quick filters">
              {TAB_OPTIONS.map((t) => (
                <TabBtn
                  key={t.value}
                  role="tab"
                  aria-selected={tab === t.value}
                  selected={tab === t.value}
                  onClick={() => {
                    setTab(t.value);
                    setPage(1);
                  }}
                >
                  {t.label}
                </TabBtn>
              ))}
            </Tabs>
            <ShowBtn
              data-testid="filterBtn"
              open={showFilters}
              aria-expanded={showFilters}
              onClick={() => setShowFilters((v) => !v)}
            >
              <FilterListIcon sx={{ fontSize: 18 }} />
              <span data-testid="filterBtnLabel">
                {showFilters ? 'Hide' : 'Show'}
              </span>
            </ShowBtn>
          </ToolbarActions>
        </Toolbar>

        {showFilters && (
          <FilterRow
            filters={filters}
            skills={skillsForRow}
            onChange={(next) => {
              setFilters(next);
              setPage(1);
            }}
          />
        )}

        {error && <div role="alert">{error}</div>}

        <Card>
          {isMobile ? (
            <MobileList>
              {workers.map((w) => (
                <WorkerCard
                  key={w.id}
                  worker={w}
                  onClick={() => navigate(`/workers_v2/${w.id}`)}
                />
              ))}
            </MobileList>
          ) : (
            <WorkersTable
              workers={workers}
              sort={sort}
              onSort={onSort}
              tierBoundaries={boundaries}
              onWorkerClick={(id) => navigate(`/workers_v2/${id}`)}
              loading={listLoading}
            />
          )}
        </Card>
        {pagination && pagination.total > 0 && (
          <Paginator
            currentPage={pagination.page}
            handlePageChange={setPage}
            handleNumberItemsChange={(e) => {
              setPage(1);
              setPerPage(parseInt(e.target.value, 10));
            }}
            itemsLength={pagination.total}
            itemsPerPage={perPage}
          />
        )}
      </Stack>
    </Page>
  );
};

export default GravyWorkers;
